/*
 * Copyright (c) 2022, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

export const SIGN_IN_PATH = '/signin';
/**
 * Sentinel `returnToUrl` meaning "the create integration page of this
 * organization's Default project", used when a Copilot prompt typed before
 * signing in is waiting to be handed over. The organization is not known while
 * the user is on the login page, so the real path cannot be built there.
 */
export const COPILOT_NEW_PATH = '/copilot-new';
/**
 * Sentinel `returnToUrl` meaning "the prebuilt integrations page of this
 * organization's Default project", used by the Explore integrations button on
 * the login landing page. Resolved the same way as {@link COPILOT_NEW_PATH},
 * and for the same reason — the organization is unknown before signing in.
 */
export const EXPLORE_INTEGRATIONS_PATH = '/explore-integrations';
export const EMBEDDED_SIGN_UP_PATH = '/signup_embedded';
export const EMBEDDED_SIGN_UP_HANDLER = '/signup_embedded_handler';
export const EMBEDDED_SIGN_UP_CONTEST = '/signup_embedded_contest';
export const USER_SETTINGS_PATH = '/account/settings';
export const PAT_PATH = `${USER_SETTINGS_PATH}/tokens`;
export const ORG_PATH = '/organizations/:orgHandle';
export const ORG_RAG_INGESTION_PATH = `${ORG_PATH}/rag/rag-ingestion`;
export const ORG_KNOWLEDGE_BASE_PATH = `${ORG_PATH}/rag/knowledge-base`;
export const ORG_RAG_RETRIEVAL_PATH = `${ORG_PATH}/rag/rag-retrieval`;
export const ORG_VOICE_AGENT_PATH = `${ORG_PATH}/ai/voice-agent`;
export const PE_PATH = `/pe-view${ORG_PATH}`;
export const HOME_PATH = `${ORG_PATH}/home`;
export const PROJECTS_PATH = `${ORG_PATH}/projects`;
export const PE_PROJECTS_PATH = `${PE_PATH}/projects`;
export const PROJECTS_OBSERVE_PATH = `${ORG_PATH}/observe`;
export const MARKETPLACE_PATH = `${ORG_PATH}/marketplace`;
export const SETTINGS_PATH = `${ORG_PATH}/settings`;
export const PROJECT_SETTINGS_PATH = `${SETTINGS_PATH}/project-overview`;
export const CONFIG_MANAGEMENT_SETTINGS_PATH = `${SETTINGS_PATH}/configmanagement`;
export const INSIGHTS_PATH = `${ORG_PATH}/insights`;
export const NEW_PROJECT_PATH = `${PROJECTS_PATH}/new`;
export const PROJECT_PATH = `${PROJECTS_PATH}/:projectHandler`;
export const PROJECT_HOME_PATH = `${PROJECT_PATH}/home`;
export const CIO_DASHBOARD_PATH = `${ORG_PATH}/cio-dashboard`;
export const CIO_DASHBOARD_PROJECT_PATH = `${PROJECT_PATH}/cio-dashboard`;
export const PROJECT_LEVEL_SETTINGS_PATH = `${PROJECT_PATH}/settings`;
export const PROJECT_OBSERVE_PATH = `${PROJECT_PATH}/observe`;
export const COMPONENTS_PATH = `${PROJECT_PATH}/components`;
export const NEW_COMPONENT_PATH = `${COMPONENTS_PATH}/new`;
export const NEW_MCP_PATH = `${COMPONENTS_PATH}/newMCP`;
export const NEW_COMPONENT_SAMPLE_PATH = `${NEW_COMPONENT_PATH}/samples`;
export const COMPONENTS_LIST_PATH = `${COMPONENTS_PATH}/list`;
export const NEW_COMPONENT_CREATE_PATH = `${COMPONENTS_PATH}/new/:templateType`;
export const COMPONENT_PATH = `${COMPONENTS_PATH}/:componentHandler`;
export const COMPONENT_DEPLOY_PATH = `${COMPONENT_PATH}/deploy`;
export const COMPONENT_DEVOPS_PATH = `${PROJECT_PATH}/devops/components/:componentHandler`;
export const MARKETPLACE_CONNECTOR_OVERVIEW_PATH =
  `${MARKETPLACE_PATH}/connector/:orgName/` +
  `:pkgName/:versionName/:moduleName/:connectorName`;
export const MARKETPLACE_TRIGGER_OVERVIEW_PATH =
  `${MARKETPLACE_PATH}/trigger/:orgName/` +
  `:pkgName/:versionName/:moduleName/:triggerName`;
export const EDITOR_PATH = `${ORG_PATH}/editor`;
export const OBSERVABILITY_SAMPLE = `${ORG_PATH}/observe/sample`;
export const AZURE_SUBSCRIPTION_CONFIG = '/configure-account/azure';
export const AZURE_SUBSCRIPTION_CONFIG_ATTACH = `${AZURE_SUBSCRIPTION_CONFIG}/attach`;
export const REGISTER_GCP_SUBSCRIPTION = '/register/gcp';
export const GCP_SUBSCRIPTION_PENDING = `${REGISTER_GCP_SUBSCRIPTION}/pending`;
export const UNREGISTER_GCP_SUBSCRIPTION = '/un-register/gcp';
export const GCP_UNSUBSCRIPTION_SUCCESS = `${UNREGISTER_GCP_SUBSCRIPTION}/success`;
export const GCP_SUBSCRIPTION_ERROR = `${REGISTER_GCP_SUBSCRIPTION}/error`;
export const REGISTER_AWS_SUBSCRIPTION = '/register/aws';
export const AWS_SUBSCRIPTION_ATTACH = `${REGISTER_AWS_SUBSCRIPTION}/attach`;
export const AWS_SUBSCRIPTION_ERROR = `${REGISTER_AWS_SUBSCRIPTION}/error`;
export const DATAPLANES_PATH = `${ORG_PATH}/dataplanes`;
export const ORG_ENVIRONMENTS_PATH = `${ORG_PATH}/environments`;
export const ORG_CLOUD_STORAGE_PATH = `${ORG_PATH}/admin/databases`;
export const ORG_ENVIRONMENTS_PIPELINES_PATH = `${ORG_PATH}/deployment-pipelines`;
export const ORG_ENVIRONMENTS_PIPELINES_NEW_PATH = `${ORG_PATH}/deployment-pipelines/new`;

export const ORG_LEVEL_INTERNAL_MARKETPLACE_PATH = `${ORG_PATH}/internal-marketplace`;
export const ORG_LEVEL_INTERNAL_MARKETPLACE_SERVICES_PATH = `${ORG_LEVEL_INTERNAL_MARKETPLACE_PATH}/services`;
// TODO: chathurangas: Try to fix the max len issue
// eslint-disable-next-line max-len
export const ORG_LEVEL_INTERNAL_MARKETPLACE_SERVICE_PATH = `${ORG_LEVEL_INTERNAL_MARKETPLACE_SERVICES_PATH}/:serviceId`;

export const ORG_LEVEL_INTERNAL_MARKETPLACE_DBS_PATH = `${ORG_LEVEL_INTERNAL_MARKETPLACE_PATH}/databases`;
export const ORG_LEVEL_INTERNAL_MARKETPLACE_DB_PATH = `${ORG_LEVEL_INTERNAL_MARKETPLACE_DBS_PATH}/:resourceId`;

export const ORG_LEVEL_INTERNAL_MARKETPLACE_CONFIG_GROUPS_PATH = `${ORG_LEVEL_INTERNAL_MARKETPLACE_PATH}/config-groups`;
// eslint-disable-next-line max-len
export const ORG_LEVEL_INTERNAL_MARKETPLACE_CONFIG_GROUP_PATH = `${ORG_LEVEL_INTERNAL_MARKETPLACE_CONFIG_GROUPS_PATH}/:resourceId`;

// eslint-disable-next-line max-len
export const ORG_LEVEL_INTERNAL_MARKETPLACE_CREATE_CONNECTION_PATH = `${ORG_LEVEL_INTERNAL_MARKETPLACE_SERVICES_PATH}/:serviceId/create-connection`;
// eslint-disable-next-line max-len
export const ORG_LEVEL_INTERNAL_MARKETPLACE_SERVICE_OVERVIEW_PATH = `${ORG_LEVEL_INTERNAL_MARKETPLACE_SERVICE_PATH}/overview`;
// eslint-disable-next-line max-len
export const ORG_LEVEL_INTERNAL_MARKETPLACE_SERVICE_DEFINITION_PATH = `${ORG_LEVEL_INTERNAL_MARKETPLACE_SERVICE_PATH}/definition`;
export const PROJECT_LEVEL_INTERNAL_MARKETPLACE_PATH = `${PROJECT_PATH}/internal-marketplace`;
export const PROJECT_LEVEL_INTERNAL_MARKETPLACE_SERVICES_PATH = `${PROJECT_LEVEL_INTERNAL_MARKETPLACE_PATH}/services`;
// eslint-disable-next-line max-len
export const PROJECT_LEVEL_INTERNAL_MARKETPLACE_SERVICE_PATH = `${PROJECT_LEVEL_INTERNAL_MARKETPLACE_SERVICES_PATH}/:serviceId`;

export const PROJECT_LEVEL_INTERNAL_MARKETPLACE_DBS_PATH = `${PROJECT_LEVEL_INTERNAL_MARKETPLACE_PATH}/databases`;
export const PROJECT_LEVEL_INTERNAL_MARKETPLACE_DB_PATH = `${PROJECT_LEVEL_INTERNAL_MARKETPLACE_DBS_PATH}/:resourceId`;

// eslint-disable-next-line max-len
export const PROJECT_LEVEL_INTERNAL_MARKETPLACE_CONFIG_GROUPS_PATH = `${PROJECT_LEVEL_INTERNAL_MARKETPLACE_PATH}/config-groups`;
// eslint-disable-next-line max-len
export const PROJECT_LEVEL_INTERNAL_MARKETPLACE_CONFIG_GROUP_PATH = `${PROJECT_LEVEL_INTERNAL_MARKETPLACE_CONFIG_GROUPS_PATH}/:resourceId`;

// eslint-disable-next-line max-len
export const PROJECT_LEVEL_INTERNAL_MARKETPLACE_CREATE_CONNECTION_PATH = `${PROJECT_LEVEL_INTERNAL_MARKETPLACE_SERVICES_PATH}/:serviceId/create-connection`;
// eslint-disable-next-line max-len
export const PROJECT_LEVEL_INTERNAL_MARKETPLACE_SERVICE_OVERVIEW_PATH = `${PROJECT_LEVEL_INTERNAL_MARKETPLACE_SERVICE_PATH}/overview`;
// eslint-disable-next-line max-len
export const PROJECT_LEVEL_INTERNAL_MARKETPLACE_SERVICE_DEFINITION_PATH = `${PROJECT_LEVEL_INTERNAL_MARKETPLACE_SERVICE_PATH}/definition`;

// Unified logs view paths
export const ORG_LEVEL_AUDIT_LOGS_PATH = `${ORG_PATH}/auditlogs`;
export const ORG_LEVEL_RUNTIME_LOGS_PATH = `${ORG_PATH}/observe/runtimelogs`;
export const PROJECT_LEVEL_RUNTIME_LOGS_PATH = `${PROJECT_PATH}/observe/runtimelogs`;
export const COMPONENT_LEVEL_RUNTIME_LOGS_PATH = `${COMPONENT_PATH}/observe/runtimelogs`;
export const CERTIFICATES_MANAGEMENT = `${ORG_PATH}/devops/certificates`;

export const generateBasePath = (orgHandle: string, subPath?: string) => {
  const path = `/organizations/${orgHandle}`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgListingPath = (orgHandle: string, subPath?: string) => {
  const path = `/organizations/${orgHandle}/listing`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateProjectsPath = (orgHandle: string, subPath?: string) => {
  const path = `/organizations/${orgHandle}/projects`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgLevelObservePath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/observe`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateMarketplacePath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/marketplace`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateApiBuildPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  return `/organizations/${orgHandle}${projectPath}${componentPath}/build`;
};

export const generateInsightPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/organizations/${orgHandle}${projectPath}${componentPath}/analytics`;
  return subPath ? `${path}/${subPath}` : path;
};

export const generateCIODashboardPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/organizations/${orgHandle}${projectPath}${componentPath}/cio-dashboard`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateDataPlanesPath = (orgHandle: string, subPath?: string) => {
  const path = `/organizations/${orgHandle}/dataplanes`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateApiGovernancePath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/organizations/${orgHandle}${projectPath}${componentPath}/api-governance/`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgLevelApiGovernancePath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/governance/`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateAuditLogsPath = (orgHandle: string, subPath?: string) => {
  const path = `/organizations/${orgHandle}/auditlogs`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgEnvironmentsPipelinesPath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/deployment-pipelines`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateSettingsPath = (orgHandle: string, subPath?: string) => {
  const path = `/organizations/${orgHandle}/settings`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgLevelOrgThemePath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/org-theming`;
  return subPath ? `${path}${subPath}` : path;
};

export const gernerateOrgLevelAPIMSettingsPath = (
  orgHandle: string,
  subPath: string
) => `/organizations/${orgHandle}/settings/apimanagement/${subPath}`;

export const generateProjectPath = (
  orgHandle: string,
  projectHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateCreateNewPath = (
  orgHandle: string,
  projectHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/components/new`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateQuickDeployPath = (
  orgHandle: string,
  projectHandler: string,
  queryParam: string
) =>
  `/organizations/${orgHandle}/projects/${projectHandler}/components/quick-deploy${queryParam}`;

export const generateProjectSettingsPath = (
  orgHandle: string,
  projectHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/settings`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateComponentSettingsPath = (
  orgHandle: string,
  projectHandler: string,
  componentHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/components/${componentHandler}/settings`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateProjectLevelObservePath = (
  orgHandle: string,
  projectHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/observe`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateComponentLevelObservePath = (
  orgHandle: string,
  projectHandler: string,
  componentHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/components/${componentHandler}/observe`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateComponentLevelListingPath = (
  orgHandle: string,
  projectHandler: string,
  componentHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/listing/components/${componentHandler}`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateComponentDevOpsPath = (
  orgHandle: string,
  projectHandler: string,
  componentHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/devops/components/${componentHandler}`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateComponentPath = (
  orgHandle: string,
  projectHandler: string,
  componentHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/components/${componentHandler}`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateNewComponentPath = (
  orgHandle: string,
  projectHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/components/new`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateComponentsListingPath = (
  orgHandle: string,
  projectHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/components/list`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateProjectArchitecturePath = (
  orgHandle: string,
  projectHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/architecture`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgArchitecturePath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/architecture`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateEditorPath = (
  orgHandle: string,
  projectHandler: string,
  componentId: string,
  componentHandler: string,
  correlationId: string
) =>
  `/organizations/${orgHandle}/editor?projectHandler=${projectHandler}&componentId=${componentId}` +
  `&compHandler=${componentHandler}&correlationId=${correlationId}`;

export const generateProjectEnvironmentsPath = (
  orgHandle: string,
  projectHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/devops/environments`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateAutomationPipelinesPath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/automation-pipelines`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgEnvironmentsPath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/environments`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateApprovalsPath = (orgHandle: string, subPath?: string) => {
  const path = `/organizations/${orgHandle}/approvals`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgCloudStoragePath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/admin/databases`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgStoragePath = (orgHandle: string, subPath?: string) => {
  const path = `/organizations/${orgHandle}/admin/storage`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgCloudVectorStoragePath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/admin/vector-databases`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgRAGIngestionPath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/rag/rag-ingestion`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgKnowledgeBasePath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/rag/knowledge-base`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgRAGRetrievalPath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/rag/rag-retrieval`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgRAGServicePath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/rag-service`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgVoiceAgentPath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/ai/voice-agent`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateGovernancePath = (orgHandle: string, subPath?: string) => {
  const path = `/organizations/${orgHandle}/governance/`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgMessageBrokersPath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/admin/message-brokers`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateOrgConfigGroupsPath = (
  orgHandle: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/devops/config-groups`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateProjectDevopsPath = (
  orgHandle: string,
  projectHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/devops`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateProjectHelmMarketplacePath = (
  orgHandle: string,
  projectHandler: string,
  subPath?: string
) => {
  const path = `/organizations/${orgHandle}/projects/${projectHandler}/devops/helm-marketplace`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateRedirectionQueryParams = (
  returnToUrlSearch: string
): string => {
  const queryParams = new URLSearchParams(returnToUrlSearch);
  return queryParams.toString();
};

export const generateInternalMarketplacePath = (orgHandle: string) =>
  `/organizations/${orgHandle}/internal-marketplace`;

export const generateOrgLevelSettingsPath = (orgHandle: string) =>
  `/organizations/${orgHandle}/settings`;

export const generateOrgLevelDependenciesPath = (orgHandle: string) =>
  `/organizations/${orgHandle}/admin`;

export const generateOrgLevelConnectionsPath = (orgHandle: string) =>
  `/organizations/${orgHandle}/connections`;

export const generateOrgLevelCertificateManagementPath = (orgHandle: string) =>
  `/organizations/${orgHandle}/devops/certificates`;

export const replaceProjectHandlerInUrl = (
  originalUrl: string,
  newProjectHandler: string
): string => {
  const urlParts = originalUrl.split('/');
  const projectIndex = urlParts.findIndex((part) => part.includes('projects'));
  if (projectIndex !== -1) {
    urlParts[projectIndex + 1] = newProjectHandler;
    return urlParts.join('/');
  }
  // eslint-disable-next-line no-console
  console.error('Project handler not found in the URL.', {
    originalUrl,
    newProjectHandler,
  });
  return originalUrl;
};

export const replaceProjectIdInUrl = (
  originalUrl: string,
  newProjectId: string
): string => {
  const urlParts = originalUrl.split('/');
  const projectIdIndex = urlParts.findIndex((part) =>
    part.includes('projects')
  );
  if (projectIdIndex !== -1) {
    urlParts[projectIdIndex + 1] = newProjectId;
    return urlParts.join('/');
  }
  // eslint-disable-next-line no-console
  console.error('ProjectId not found in the URL.', {
    originalUrl,
    newProjectId,
  });
  return originalUrl;
};

export const replaceOrgHandleInUrl = (
  originalUrl: string,
  newOrgHandle: string
): string => {
  const urlParts = originalUrl.split('/');
  const orgHandleIndex = urlParts.findIndex((part) =>
    part.includes('organizations')
  );
  if (orgHandleIndex !== -1) {
    urlParts[orgHandleIndex + 1] = newOrgHandle;
    return urlParts.join('/');
  }
  // eslint-disable-next-line no-console
  console.error('Org handle not found in the URL.', {
    originalUrl,
    newOrgHandle,
  });
  return originalUrl;
};

// Platform Engineer Perspective Routes
export const generatePEBasePath = (orgHandle: string, subPath?: string) => {
  const path = `/pe-view/organizations/${orgHandle}`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePEProjectPath = (
  orgHandle: string,
  projectHandler: string,
  subPath?: string
) => {
  const path = `/pe-view/organizations/${orgHandle}/projects/${projectHandler}`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePEOverviewPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/pe-view/organizations/${orgHandle}${projectPath}${componentPath}/overview`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePEInfrastructurePath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/pe-view/organizations/${orgHandle}${projectPath}${componentPath}/infrastructure`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePEDevOpsPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/pe-view/organizations/${orgHandle}${projectPath}${componentPath}/devops`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePECDPipelinesPath = (
  orgHandle: string,
  projectHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const path = `/pe-view/organizations/${orgHandle}/devops${projectPath}/cd-pipelines`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePEZeroTrustPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/pe-view/organizations/${orgHandle}${projectPath}${componentPath}/zero-trust`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePEOpsCenterPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/pe-view/organizations/${orgHandle}${projectPath}${componentPath}/ops-center`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePEAlertsAndObsPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/pe-view/organizations/${orgHandle}${projectPath}${componentPath}/alerts-obs`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePEGovernancePath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/pe-view/organizations/${orgHandle}${projectPath}${componentPath}/governance`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePEResourcesPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/pe-view/organizations/${orgHandle}${projectPath}${componentPath}/resources`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePEInsightsPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/pe-view/organizations/${orgHandle}${projectPath}${componentPath}/insights`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePEUserMgmtPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/pe-view/organizations/${orgHandle}${projectPath}${componentPath}/user-management`;
  return subPath ? `${path}${subPath}` : path;
};

export const generatePESettingsPath = (
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/pe-view/organizations/${orgHandle}${projectPath}${componentPath}/api-management`;
  return subPath ? `${path}${subPath}` : path;
};

export const generateCombinedInsightsSubPath = (
  subPathType: string,
  orgHandle: string,
  projectHandler?: string,
  componentHandler?: string,
  subPath?: string
) => {
  const projectPath = projectHandler ? `/projects/${projectHandler}` : '';
  const componentPath = componentHandler
    ? `/components/${componentHandler}`
    : '';
  const path = `/organizations/${orgHandle}${projectPath}${componentPath}/analytics/${subPathType}`;
  return subPath ? `${path}/${subPath}` : path;
};
